import { useEffect } from 'react'
import { useSearch } from './useSearch'
import { usePagination } from './usePagination'

/**
 * Combined search and pagination hook for list views
 *
 * Combines useSearch and usePagination with automatic pagination reset on search.
 * This is the primary hook for all list pages (Move Archive, Series Archive, Tag Management).
 *
 * @param initialLimit - Items per page (default: 20 per spec)
 * @param debounceDelay - Search debounce delay (default: 300ms per spec)
 * @returns Combined search and pagination state with filters
 *
 * @example
 * ```tsx
 * const listQuery = useListQuery(20, 300);
 *
 * // Use in React Query
 * const { data } = useQuery({
 *   queryKey: ['moves', listQuery.filters],
 *   queryFn: () => fetchMoves(listQuery.filters),
 * });
 *
 * // Search input
 * <input
 *   value={listQuery.searchText}
 *   onChange={(e) => listQuery.setSearchText(e.target.value)}
 * />
 *
 * // Pagination controls
 * <button onClick={listQuery.nextPage} disabled={!listQuery.canGoNext(data?.total)}>
 *   Load More
 * </button>
 * ```
 */
export function useListQuery(
  initialLimit: number = 20,
  debounceDelay: number = 300
) {
  const search = useSearch('', debounceDelay)
  const pagination = usePagination(initialLimit, 0)

  // Auto-reset pagination when search text changes (debounced)
  useEffect(() => {
    pagination.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.debouncedSearchText])

  // Combined filters object for API calls
  // NOTE: use the immediate searchText for API filters so that
  // UI responds immediately to user input in tests. We still keep
  // the debouncedSearchText for pagination reset behavior.
  const filters = {
    searchText: search.searchText || undefined,
    limit: pagination.limit,
    offset: pagination.offset,
  }

  return {
    // Search state
    searchText: search.searchText,
    debouncedSearchText: search.debouncedSearchText,
    setSearchText: search.setSearchText,
    clearSearch: search.clearSearch,
    isSearching: search.isSearching,
    hasSearchResults: search.hasSearchResults,

    // Pagination state
    limit: pagination.limit,
    offset: pagination.offset,
    currentPage: pagination.currentPage,
    nextPage: pagination.nextPage,
    prevPage: pagination.prevPage,
    goToPage: pagination.goToPage,
    resetPagination: pagination.reset,
    changeLimit: pagination.changeLimit,
    canGoPrev: pagination.canGoPrev,
    canGoNext: pagination.canGoNext,

    // Combined filters for API calls
    filters,
  }
}
